import { Button, Divider, Flex, Input, Row, Switch, Tooltip, Typography } from 'antd';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { questionThemeType } from '../../types';

const { Text } = Typography;
const { Search } = Input;

const StyledDivider = styled(Divider)`
  margin: 16px 0;
`;

function ThemePicker({
  themes = [],
  switchTooltip,
  editable = false,
  handleEdit = () => {},
  deselectedThemes = [],
  onChange = () => {},
  allowSubthemesOnly = true,
  startSelected = true,
}) {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredThemeIds = themes.flatMap((t) => [
    ...(t.data.name.toLowerCase().includes(searchTerm.toLowerCase()) ? [t.data.id] : []),
    ...t.children
      .filter((c) => c.data.name.toLowerCase().includes(searchTerm.toLowerCase()))
      .map((c) => c.data.id),
  ]);

  const selectedThemes = useMemo(
    () =>
      themes.reduce(
        (acc, curr) => ({
          ...acc,
          [curr.data.id]: !deselectedThemes.includes(curr.data.id),
          ...curr.children.reduce(
            (a, c) => ({
              ...a,
              [c.data.id]: !deselectedThemes.includes(c.data.id),
            }),
            {},
          ),
        }),
        {},
      ),
    [themes, deselectedThemes],
  );

  const onThemeChange = (id, checked) => {
    let newThemes;
    if (checked) {
      newThemes = deselectedThemes.filter((tId) => tId !== id);
    } else {
      const theme = themes.find((t) => t.data.id === id);
      const subthemesIds = theme.children.map((c) => c.data.id);
      newThemes = [...deselectedThemes, id, ...subthemesIds];
    }
    onChange(newThemes);
  };

  const onSubthemeChange = (themeId, id, checked) => {
    let newThemes;
    if (checked) {
      newThemes = deselectedThemes.filter(
        (tId) => tId !== id && (allowSubthemesOnly || tId !== themeId),
      );
    } else {
      newThemes = [...deselectedThemes, id];
    }
    onChange(newThemes);
  };

  const onReset = () => {
    if (startSelected) {
      onChange([]);
    } else {
      const themeIds = themes.flatMap((t) => [t.data.id, ...t.children.map((c) => c.data.id)]);
      onChange(themeIds);
    }
  };

  const getThemeLabel = (theme, type = 'default') => (
    <Text
      type={type}
      editable={
        editable
          ? {
              onChange: (e) => {
                handleEdit(theme, e);
              },
            }
          : false
      }
    >
      {theme.data.name}
    </Text>
  );

  return (
    <Flex vertical gap={16}>
      <Flex justify="space-between" gap={8}>
        <Search value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} allowClear />
        <Button color="danger" variant="outlined" onClick={onReset}>
          Reset
        </Button>
      </Flex>
      <div>
        {themes?.map((t, idx) => (
          <React.Fragment key={t.data.id}>
            {filteredThemeIds.includes(t.data.id) && (
              <>
                {idx !== 0 && <StyledDivider />}
                <Row justify="space-between" wrap={false} align="middle">
                  {getThemeLabel(t)}
                  <Tooltip title={switchTooltip} placement="right">
                    <Switch
                      size="small"
                      checked={selectedThemes[t.data.id]}
                      onChange={(checked) => onThemeChange(t.data.id, checked)}
                    />
                  </Tooltip>
                </Row>
              </>
            )}
            {t.children
              .filter((c) => filteredThemeIds.includes(c.data.id))
              .map((c) => (
                <React.Fragment key={c.data.id}>
                  <StyledDivider />
                  <Row justify="space-between" wrap={false} align="middle">
                    {getThemeLabel(c, 'secondary')}
                    <Tooltip title={switchTooltip} placement="right">
                      <Switch
                        size="small"
                        checked={selectedThemes[c.data.id]}
                        onChange={(checked) => onSubthemeChange(t.data.id, c.data.id, checked)}
                      />
                    </Tooltip>
                  </Row>
                </React.Fragment>
              ))}
          </React.Fragment>
        ))}
      </div>
    </Flex>
  );
}

ThemePicker.propTypes = {
  themes: PropTypes.arrayOf(questionThemeType),
  switchTooltip: PropTypes.string.isRequired,
  editable: PropTypes.bool,
  handleEdit: PropTypes.func,
  deselectedThemes: PropTypes.arrayOf(PropTypes.number),
  onChange: PropTypes.func,
  allowSubthemesOnly: PropTypes.bool,
  startSelected: PropTypes.bool,
};

export default ThemePicker;
